import { useState } from 'react';
import { Plus, Building2, ChevronRight, Trash2, Edit2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from '../ui/card';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '../ui/dialog';
import chepLogo from '../../assets/images/CHEP_Logo.png';

export interface Customer {
    id: string;
    name: string;
    industry: string;
    addedDate: string;
}

interface CustomerSelectionProps {
    customers: Customer[];
    onSelectCustomer: (customer: Customer) => void;
    onAddCustomer: (customer: Customer) => void;
    onDeleteCustomer: (customerId: string) => void;
}

export function CustomerSelection({
    customers,
    onSelectCustomer,
    onAddCustomer,
    onDeleteCustomer,
}: CustomerSelectionProps) {
    const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
    const [newCustomer, setNewCustomer] = useState({
        name: '',
        industry: '',
    });

    const handleAddCustomer = () => {
        if (newCustomer.name && newCustomer.industry) {
            const customer: Customer = {
                id: Date.now().toString(),
                name: newCustomer.name,
                industry: newCustomer.industry,
                addedDate: new Date().toLocaleDateString(),
            };
            onAddCustomer(customer);
            setNewCustomer({ name: '', industry: '' });
            setIsAddDialogOpen(false);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-8">
            <div className="w-full max-w-5xl">
                {/* Header */}
                <div className="text-center mb-8">
                    <div className="flex items-center justify-center gap-4 mb-4">
                        <img
                            src={chepLogo}
                            alt="CHEP Logo"
                            className="h-16"
                            style={{ mixBlendMode: 'multiply' }}
                        />
                    </div>
                    <h2 className="text-2xl text-gray-900 mb-2">Customer Management</h2>
                    <p className="text-gray-600">
                        Select a customer to manage their dashboard data
                    </p>
                </div>

                {/* Add Customer Button */}
                <div className="mb-6 flex justify-end">
                    <Button
                        onClick={() => setIsAddDialogOpen(true)}
                        className="gap-2"
                    >
                        <Plus className="w-4 h-4" />
                        Add New Customer
                    </Button>
                </div>

                {/* Customer List */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {customers.map((customer) => (
                        <Card
                            key={customer.id}
                            className="hover:shadow-lg transition-all duration-200 cursor-pointer group relative"
                        >
                            <CardHeader className="pb-14">
                                <div className="flex items-start justify-between">
                                    <div className="flex items-center gap-3">
                                        <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center text-[#0072CE] group-hover:bg-blue-100 transition-colors">
                                            <Building2 className="w-6 h-6" />
                                        </div>
                                        <div>
                                            <CardTitle className="text-xl font-normal">{customer.name}</CardTitle>
                                            <CardDescription className="text-xs mt-1">
                                                {customer.industry}
                                            </CardDescription>
                                        </div>
                                    </div>
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            if (confirm(`Delete ${customer.name}?`)) {
                                                onDeleteCustomer(customer.id);
                                            }
                                        }}
                                        className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-red-50 rounded"
                                    >
                                        <Trash2 className="w-4 h-4 text-red-600" />
                                    </button>
                                </div>
                            </CardHeader>
                            <CardContent>
                                <div className="flex items-center justify-between text-md text-gray-500 mb-3">
                                    <span>Added: {customer.addedDate}</span>
                                </div>
                                <Button
                                    onClick={() => onSelectCustomer(customer)}
                                    variant="outline"
                                    className="w-full gap-2 group-hover:bg-[#0072CE] group-hover:text-white group-hover:border-[#0072CE] transition-colors text-md font-normal"
                                >
                                    Manage Dashboard
                                    <ChevronRight className="w-4 h-4" />
                                </Button>
                            </CardContent>
                        </Card>
                    ))}

                    {customers.length === 0 && (
                        <div className="col-span-full text-center py-12">
                            <Building2 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                            <h3 className="text-lg text-gray-900 mb-2">No customers yet</h3>
                            <p className="text-gray-600 mb-4">
                                Add your first customer to start managing their dashboard data
                            </p>
                            <Button onClick={() => setIsAddDialogOpen(true)} className="gap-2">
                                <Plus className="w-4 h-4" />
                                Add Customer
                            </Button>
                        </div>
                    )}
                </div>
            </div>

            {/* Add Customer Dialog */}
            <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Add New Customer</DialogTitle>
                        <DialogDescription>
                            Enter the customer details to create a new dashboard
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-4 mt-4">
                        <div>
                            <Label htmlFor="customer-name">Customer Name</Label>
                            <Input
                                id="customer-name"
                                value={newCustomer.name}
                                onChange={(e) =>
                                    setNewCustomer({ ...newCustomer, name: e.target.value })
                                }
                                placeholder="e.g., Walmart Canada"
                            />
                        </div>

                        <div>
                            <Label htmlFor="customer-industry">Industry</Label>
                            <Input
                                id="customer-industry"
                                value={newCustomer.industry}
                                onChange={(e) =>
                                    setNewCustomer({ ...newCustomer, industry: e.target.value })
                                }
                                placeholder="e.g., Retail, Manufacturing, Food Service"
                            />
                        </div>

                        <div className="flex justify-end gap-3 mt-6">
                            <Button
                                variant="outline"
                                onClick={() => {
                                    setIsAddDialogOpen(false);
                                    setNewCustomer({ name: '', industry: '' });
                                }}
                            >
                                Cancel
                            </Button>
                            <Button onClick={handleAddCustomer}>Add Customer</Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
